import type { InfoCard } from '@everyturn/engine/contracts';
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

export interface GameInfo {
  gameId: string;
  displayName: string;
  playerCount: number;
  infoCards?: InfoCard[];
}

export interface PlayModeState {
  /** Info about the current game, if playing. */
  gameInfo: GameInfo | null;
  /** Call to enter play mode (game loaded and ready). */
  enterPlayMode: (opts: { gameInfo?: GameInfo }) => void;
  /** Call when play mode ends (game over, error, etc.). */
  exitPlayMode: () => void;
  /** Open the game info dialog (conventions, match format, UI help). */
  openGameInfo: (returnFocus: HTMLElement | null) => void;
  gameInfoTrigger: HTMLElement | null;
  /** Whether the game info dialog is open. */
  gameInfoOpen: boolean;
  /** Set game info dialog open state. */
  setGameInfoOpen: (open: boolean) => void;
}

const PlayModeContext = createContext<PlayModeState | null>(null);

export function PlayModeProvider({ children }: { children: ReactNode }) {
  const [gameInfo, setGameInfo] = useState<GameInfo | null>(null);
  const [gameInfoOpen, setGameInfoOpen] = useState(false);
  const [gameInfoTrigger, setGameInfoTrigger] = useState<HTMLElement | null>(null);

  const enterPlayMode = useCallback((opts: { gameInfo?: GameInfo }) => {
    setGameInfo(opts.gameInfo ?? null);
  }, []);

  const exitPlayMode = useCallback(() => {
    setGameInfo(null);
    setGameInfoOpen(false);
    setGameInfoTrigger(null);
  }, []);

  const openGameInfo = useCallback((returnFocus: HTMLElement | null) => {
    setGameInfoTrigger(returnFocus);
    setGameInfoOpen(true);
  }, []);

  return (
    <PlayModeContext
      value={{
        gameInfo,
        enterPlayMode,
        exitPlayMode,
        openGameInfo,
        gameInfoOpen,
        gameInfoTrigger,
        setGameInfoOpen,
      }}
    >
      {children}
    </PlayModeContext>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePlayMode(): PlayModeState {
  const ctx = useContext(PlayModeContext);
  if (!ctx) throw new Error('usePlayMode must be used within PlayModeProvider');
  return ctx;
}
